import { useDimensions } from "@repo/core/hooks/useDimensions";
import {
  AccessorKeyColumnDef,
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable
} from "@tanstack/react-table";

import { Text } from "@repo/core/components/core/Text";
import { convertDataRows } from "@repo/core/components/core/DataTable/utils";
import { TableConvertedData } from "@repo/core/components/core/DataTable/types";
import { exportTanstackTableToCSV } from "@repo/core/utils/csv.utils";
import { useMemo, useRef, useState } from "react";
import { IModalRefProps } from "@repo/core/components/layout/Modal/types";
import { format } from "date-fns";
import { ReportType, ReportTypeEnum } from "../../types";
import { useDeviceType } from "@repo/core/hooks/useDeviceType";
import { ROUTES } from "@repo/core/routes";
import { useNavigation } from "@repo/core/hooks/useNavigation";
import { ReportsResource } from "@repo/core/services/resources/reports";
import { newApInstance } from "@repo/core/services/instances/new-api";
import { ReasonDenialReportTypes } from "@repo/core/types/reports";
import { useReportsFilters } from "@repo/core/hooks/useReportsFilters";
import { useQuery } from "@tanstack/react-query";

const reportsResource = new ReportsResource(newApInstance);

export const ReasonDenialReportController = () => {
  const { isMobile } = useDimensions();

  const { isMobile: isMobileDevice } = useDeviceType();
  const { push } = useNavigation();

  const [selectedReportType, setSelectedReportType] =
    useState<ReasonDenialReportTypes>(ReportTypeEnum.PERCENTAGE);

  const {
    selectedAttendant,
    selectedProduct,
    selectedStore,
    filterStartDate,
    filterEndDate,
    onChangeStartDate,
    onChangeEndDate
  } = useReportsFilters();

  const filterModalRef = useRef<IModalRefProps>(null);

  const columnHelper = createColumnHelper();

  const {
    data: reasonDenialReportResponse,
    isFetching: isFetchingReasonDenialReport
  } = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [
      "reasonDenial",
      filterStartDate,
      filterEndDate,
      selectedStore,
      selectedProduct,
      selectedAttendant,
      selectedReportType
    ],
    queryFn: () =>
      reportsResource.getReasonDenialReports({
        startDate: format(filterStartDate, "yyyy-MM-dd"),
        endDate: format(filterEndDate, "yyyy-MM-dd"),
        store: selectedStore,
        product: selectedProduct,
        attendant: selectedAttendant,
        type: selectedReportType
      })
  });

  const onSelectReportType = (reportType: ReportTypeEnum) => {
    setSelectedReportType(reportType as ReasonDenialReportTypes);
  };

  const reportTypes: ReportType[] = [
    {
      title: "Percentual",
      type: ReportTypeEnum.PERCENTAGE
    },
    {
      title: "Quantidade",
      type: ReportTypeEnum.QUANTITY
    }
  ];

  const headerColumns = reasonDenialReportResponse?.data.table.headers.map(
    (header) => {
      return columnHelper.accessor(header.key, {
        header: () => (
          <Text
            fontSize="$xs"
            color={header?.styles?.text?.color || "$textLight700"}
            fontWeight={header?.styles?.text?.fontWeight || 600}
          >
            {header.label}
          </Text>
        ),
        enableSorting: false,
        enableColumnFilter: false,
        meta: header?.styles
      });
    }
  );

  const { tableData, chartBarData, cardsData, hasReportData } = useMemo(() => {
    const tableData = convertDataRows(
      reasonDenialReportResponse?.data?.table.headers,
      reasonDenialReportResponse?.data?.table.rows
    );

    const chartBarData = reasonDenialReportResponse?.data?.charts?.find(
      (chart) => chart.type === "bar"
    );

    const cardsData = reasonDenialReportResponse?.data?.mobileTable;

    const hasReportData =
      Array.isArray(reasonDenialReportResponse?.data?.table.rows) &&
      reasonDenialReportResponse.data.table.rows.length > 0;

    return {
      tableData,
      chartBarData,
      cardsData,
      hasReportData
    };
  }, [reasonDenialReportResponse?.data]);

  const table = useReactTable({
    data: tableData,
    columns: headerColumns as AccessorKeyColumnDef<TableConvertedData>[],
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel()
  });

  const topRightText = useMemo(() => {
    return `Atualizado em ${format(new Date(), "dd/MM")} às ${format(new Date(), "HH:mm:ss")}`;
  }, []);

  const handleExportTable = () => {
    exportTanstackTableToCSV(
      "Relatório_Vendas_Mensais",
      reasonDenialReportResponse?.data.table.headers,
      table
    );
  };

  const handleSelectFilters = () => {
    if (isMobileDevice) {
      push(ROUTES.reportSelectFilters);
    } else {
      if (filterModalRef.current) {
        filterModalRef.current.open();
      }
    }
  };

  return {
    isMobile,
    onChangeStartDate,
    onChangeEndDate,
    table,
    cardsData,
    chartBarData,
    handleExportTable,
    filterModalRef,
    topRightText,
    reportTypes,
    selectedReportType,
    onSelectReportType,
    filterStartDate,
    filterEndDate,
    hasReportData,
    handleSelectFilters,
    isFetchingReasonDenialReport
  };
};
